import React, { useEffect, useState } from 'react';
import ButtonStartIcon from '../../../../../button/ButtonStartIcon';
import Typography from '@mui/material/Typography';
import InputField from '../../../../../FormControl/input/InputField';
import Dialog from '../../../../../dialog/Dialog';
import useFlags from '../../../../../../hooks/setting/useFlags';
import { Alert, Tooltip } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useSelector } from 'react-redux';
import InputIcon from '../../../../../FormControl/input/InputIcon';
import InputColor from '../../../../../FormControl/input/InputColor';

const FlagDialog = ({ show, onClose, type, data = {} }) => {
  const { errors: errorsData, createFlag, updateFlag, deleteFlag } = useFlags();
  const [form, setInputForm] = useState({});
  const [errors, setInputErrors] = useState(errorsData);
  const [loading, toggleLoading] = useState(false);
  const sessionUserRoles = useSelector((state) => state.session?.user?.roles?.map((item) => item.id) || []);

  const onChange = (name, value) => {
    setInputForm({ ...form, [name]: value });
    setInputErrors({ ...errors, [name]: null });
  };

  const onSave = async (e) => {
    if (!['create', 'edit', 'delete'].includes(type)) {
      setInputErrors({ message: 'Invalid type' });
      return;
    }
    e.preventDefault();
    toggleLoading(true);
    try {
      let result = { error: false };
      if (type === 'create') {
        result = await createFlag(form);
      }
      if (type === 'edit') {
        result = await updateFlag(form._id, form);
      }
      if (type === 'delete') {
        result = await deleteFlag(form._id);
      }
      if (result.error) {
        setInputErrors(result);
      } else {
        onClose();
      }
    } finally {
      toggleLoading(false);
    }
  };

  useEffect(() => {
    setInputErrors(errorsData);
  }, [errorsData]);

  useEffect(() => {
    setInputForm(data || {});
  }, [data]);

  const Actions = (
    <>
      <ButtonStartIcon
        onClick={onClose}
        color='primary'
        variant='outlined'
        disabled={loading}
        startIcon={['fal', 'times']}
      >
        Cancel
      </ButtonStartIcon>
      {['create', 'edit', 'delete'].includes(type) && (
        <ButtonStartIcon
          onClick={onSave}
          color='primary'
          variant='contained'
          disabled={loading}
          loading={loading}
          startIcon={['fal', 'check']}
        >
          {type === 'delete' ? 'Confirm' : 'Save'}
        </ButtonStartIcon>
      )}
    </>
  );

  return (
    <Dialog id={`${type}-flag`} show={show} title={`${type} flag`} onClose={onClose} actions={Actions}>
      {type === 'delete' ? (
        <Typography>
          Are you sure you want to delete the flag: <b>{form.name}</b>?
        </Typography>
      ) : (
        <div className='flex flex-col gap-4 py-4'>
          <div className='grid grid-cols-3 items-center gap-4'>
            <Typography variant='inherit' className='font-bold' color='primary' align='right' component='p'>
              Name
            </Typography>
            <div className='col-span-2'>
              {type === 'preview' ? (
                <Typography>{form.name}</Typography>
              ) : (
                <InputField
                  id='name'
                  name='name'
                  value={form.name}
                  error={errors?.name}
                  helperText={errors?.name}
                  onChange={(e) => onChange('name', e.target.value)}
                  fullWidth
                />
              )}
            </div>
            {sessionUserRoles.includes('Maestro') && (
              <>
                <div className='flex gap-2 items-center justify-end'>
                  <Tooltip arrow title='This name will be used to import data from other sources'>
                    <FontAwesomeIcon icon={['fal', 'question-circle']} className='text-primary' />
                  </Tooltip>
                  <Typography
                    variant='inherit'
                    className='font-bold'
                    color='primary'
                    align='right'
                    component=''
                  >
                    Import Name
                  </Typography>
                </div>

                <div className='col-span-2'>
                  {type === 'preview' ? (
                    <Typography>{form.importName}</Typography>
                  ) : (
                    <InputField
                      id='importName'
                      name='importName'
                      value={form.importName}
                      error={errors?.importName}
                      helperText={errors?.importName}
                      onChange={(e) => onChange('importName', e.target.value)}
                      fullWidth
                    />
                  )}
                </div>
              </>
            )}
            <Typography variant='inherit' className='font-bold' color='primary' align='right' component='p'>
              Icon
            </Typography>
            <div className='col-span-2'>
              <InputIcon
                id='icon'
                value={form.icon}
                error={errors?.icon}
                disabled={type === 'preview'}
                onChange={(value) => onChange('icon', value)}
                fullWidth
              />
            </div>
            <Typography variant='inherit' className='font-bold' color='primary' align='right' component='p'>
              Color
            </Typography>
            <div className='col-span-2'>
              <InputColor
                id='color'
                name='color'
                type='color'
                value={form.color}
                error={errors?.color}
                disabled={type === 'preview'}
                onChange={(value) => onChange('color', value)}
                fullWidth
              />
            </div>
          </div>
        </div>
      )}
      {errors && errors.message && (
        <Alert severity='error' onClose={() => setInputErrors({ message: null })}>
          {errors.message}
        </Alert>
      )}
    </Dialog>
  );
};

export default FlagDialog;
